import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Lock } from 'lucide-react';
import BookingModal from './BookingModal';

const EMR_LOGIN_URL = 'https://iv-appointments.emergent.host/login';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Membership', path: '/membership' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  const openEMRLogin = () => {
    window.open(EMR_LOGIN_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <nav className="fixed w-full z-50 bg-black/95 backdrop-blur-sm border-b border-yellow-500/20">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo + tagline */}
            <Link to="/" className="flex items-center space-x-3">
              <img
                src="https://customer-assets.emergentagent.com/job_elite-client-growth/artifacts/l293bfcb_Eliteiv%20logo%20No%20Number%20or%20website.png"
                alt="EliteIV Lounge"
                className="h-20 w-auto"
                onError={(e) => {
                  console.error('Logo failed to load');
                  e.target.style.display = 'none';
                }}
              />
              <div className="block md:hidden xl:block border-l border-yellow-500/40 pl-3 pr-4 leading-tight max-w-[210px] xl:max-w-none xl:whitespace-nowrap" data-testid="nav-tagline">
                <p className="font-gothic text-yellow-400 text-xs md:text-sm tracking-wider">
                  EliteIV Mobile IV Therapy
                </p>
                <p className="text-gray-300 text-[11px] md:text-xs mt-0.5">
                  Delivering exceptional patient care to the Tampa Bay Area &amp; Pinellas County
                </p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4 xl:space-x-7 whitespace-nowrap">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive(link.path)
                      ? 'text-yellow-400'
                      : 'text-gray-300 hover:text-yellow-400'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <button
                onClick={() => setShowBooking(true)}
                className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-5 xl:px-6 py-2.5 rounded-full font-semibold hover:from-yellow-500 hover:to-yellow-700 transition-all btn-gold-hover whitespace-nowrap"
                data-testid="nav-book-now-btn"
              >
                Book Now
              </button>
              <button
                onClick={openEMRLogin}
                className="flex items-center space-x-2 border-2 border-yellow-400 text-yellow-400 px-4 py-2 rounded-full font-medium hover:bg-yellow-400 hover:text-black transition-all whitespace-nowrap"
                data-testid="staff-login-btn"
              >
                <Lock className="w-4 h-4" />
                <span>Staff Login</span>
              </button>
              <a
                href="tel:727-503-2572"
                className="flex items-center space-x-2 text-yellow-400 hover:text-yellow-300 transition-colors whitespace-nowrap"
              >
                <Phone className="w-5 h-5" />
                <span className="font-medium">727-503-2572</span>
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden text-yellow-400"
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="md:hidden pb-4">
              <div className="flex flex-col space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive(link.path)
                        ? 'text-yellow-400'
                        : 'text-gray-300 hover:text-yellow-400'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <button
                  onClick={() => {
                    setShowBooking(true);
                    setIsOpen(false);
                  }}
                  className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-6 py-2.5 rounded-full font-semibold text-center"
                >
                  Book Now
                </button>
                <button
                  onClick={() => {
                    openEMRLogin();
                    setIsOpen(false);
                  }}
                  className="flex items-center justify-center space-x-2 border-2 border-yellow-400 text-yellow-400 px-4 py-2 rounded-full font-medium"
                  data-testid="mobile-staff-login-btn"
                >
                  <Lock className="w-4 h-4" />
                  <span>Staff Login</span>
                </button>
                <a
                  href="tel:727-503-2572"
                  className="flex items-center space-x-2 text-yellow-400 justify-center"
                >
                  <Phone className="w-5 h-5" />
                  <span className="font-medium">727-503-2572</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      <BookingModal isOpen={showBooking} onClose={() => setShowBooking(false)} />
    </>
  );
};

export default Navbar;